import { IPaymentContract } from "@features/payment/ui";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRouterContract } from "@features/shared/router";
import { IUserAuthContract } from "@features/student/auth/model";
import { createEvent, createStore, sample } from "effector";
import { checkUpsellPurchased, UpsellStatus } from "@features/helpers";
import { IUpsellContract } from "../model";
import { routePathNameChanged } from "@features/app/model";
import { interval, reset } from "patronum";
import { ISubscriptionContract } from "@features/subscription/model";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IUpsellUIContract } from ".";
import { CREATIVE_UPSELLS, CreativeFamily, getChainCreativeFamily, getCreativeFamily } from "@features/upsell/creative";

// The two downloadable products of the drama / film funnels. The skill is sold
// on its own screen (plus the sale one) and the income kit on its own; the
// family's Complete Bundle grants both.
export type CreativeProduct = "skill" | "incomeKit";

const getGrantingIds = (family: CreativeFamily, product: CreativeProduct): string[] => {
    const ids = CREATIVE_UPSELLS[family];
    return product === "skill" ? [ids.skill, ids.skillSale, ids.bundle] : [ids.incomeKit, ids.bundle];
};

const getScreenProduct = (upsellId: string | null): CreativeProduct | null => {
    const family = getCreativeFamily(upsellId);
    if (!family) return null;
    const ids = CREATIVE_UPSELLS[family];
    if (upsellId === ids.skill || upsellId === ids.skillSale) return "skill";
    if (upsellId === ids.incomeKit) return "incomeKit";
    return null;
};

const buyButtonClicked = createEvent();
const buyButtonDisabled = createEvent();
const buyButtonReset = createEvent();
const discountModalOpened = createEvent();
const discountModalClosed = createEvent();
const pollingStarted = createEvent();
const pollingStopped = createEvent();

const $isDiscountModalOpen = createStore(false)
    .on(discountModalOpened, () => true)
    .on(discountModalClosed, () => false);
// The family of the student's chain; null outside the drama / film funnels.
const $family = createStore<CreativeFamily | null>(null);
const $upsellIds = createStore<string[]>([]);
const $isBuyButtonDisabled = createStore(true)
    .on(buyButtonClicked, () => true)
    .on(buyButtonDisabled, () => true)
    .on(buyButtonReset, () => false);

const { tick } = interval({
    timeout: 10000,
    start: pollingStarted,
    stop: pollingStopped,
    leading: false,
    trailing: false
});

const $statuses = createStore<Record<CreativeProduct, UpsellStatus | null>>({ skill: null, incomeKit: null });
const checkRequested = createEvent();
const checkFinished = createEvent();
const $isCheckRequested = createStore(false).on(checkRequested, () => true);
const $isCheckPending = createStore(false)
    .on(checkRequested, () => true)
    .on(checkFinished, () => false);

const contract = new ModelContract(
    { $isDiscountModalOpen, $isBuyButtonDisabled, $family, $statuses },
    { buyButtonClicked, discountModalOpened, discountModalClosed },
    {},
    {}
);

export type ICreativePacksContract = typeof contract;

export class CreativePacksModule extends ModuleDescription<ICreativePacksContract> {
    constructor(
        public authModule: IUserAuthContract,
        public routerModule: IRouterContract,
        public paymentModule: IPaymentContract,
        public upsellModule: IUpsellContract,
        public upsellUiModule: IUpsellUIContract,
        public subscriptionsModule: ISubscriptionContract,
        public routesModule: IAppRouteUnitsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            source: this.upsellModule.stores.$upsells,
            fn: (upsells) => getChainCreativeFamily(upsells),
            target: $family
        });

        sample({
            source: $family,
            fn: (family) => (family ? Object.values(CREATIVE_UPSELLS[family]) : []),
            target: $upsellIds
        });

        sample({
            clock: buyButtonClicked,
            target: this.upsellModule.events.upsellBought
        });

        // Skip during the first-login funnel: the shared pipeline emits
        // upsellSaleModalOpened when the next step is the skill's sale screen.
        sample({
            clock: this.upsellUiModule.events.upsellSaleModalOpened,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId, family: $family },
            filter: (s) => s.family !== null && s.currentUpsellId === CREATIVE_UPSELLS[s.family].skill,
            target: discountModalOpened
        });

        // "Got it" moves the user to the sale offer.
        sample({
            clock: discountModalClosed,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId, family: $family },
            filter: (s) => s.family !== null && s.currentUpsellId === CREATIVE_UPSELLS[s.family].skill,
            fn: (s) => `/upsell/${CREATIVE_UPSELLS[s.family!].skillSale}`,
            target: this.routerModule.events.routerReplace
        });

        // The purchase status drives the screens' buy button and the profile's
        // downloads. The bundle screen checks its own status.
        sample({
            source: {
                upsellIds: $upsellIds,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                isHomeRoute: this.routesModule.stores.home.$isActive,
                isProfileRoute: this.routesModule.stores.profile.$isActive,
                isLibraryRoute: this.routesModule.stores.library.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isChallengesRoute: this.routesModule.stores.challenges.$isActive,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                isCheckRequested: $isCheckRequested,
                isCheckPending: $isCheckPending
            },
            filter: (s) =>
                s.upsellIds.length > 0 &&
                (s.isHomeRoute ||
                    s.isProfileRoute ||
                    // Desktop right rail shows the downloads on these routes too.
                    s.isLibraryRoute ||
                    s.isAiToolsRoute ||
                    s.isChallengesRoute ||
                    (s.isUpsellRoute && getScreenProduct(s.currentUpsellId) !== null)) &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem) &&
                !s.isCheckRequested &&
                !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.done,
            source: {
                family: $family,
                upsellIds: $upsellIds,
                awaitingVerdict: this.upsellModule.stores.$upsellIdsAwaitingVerdict
            },
            filter: (s, { params }) =>
                s.family !== null &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: (s, { result }) => {
                const statusOf = (product: CreativeProduct): UpsellStatus => {
                    const upsellIds = getGrantingIds(s.family!, product);
                    const statuses = upsellIds.map((upsellId) => checkUpsellPurchased(result, upsellId));
                    if (statuses.includes("Success")) return "Success";
                    if (statuses.includes("Processing")) return "Processing";
                    // A just-bought pack in 3ds_verify reads as "Fail" until its bank
                    // dialog is done; it is in progress, not unpurchased.
                    if (upsellIds.some((upsellId) => s.awaitingVerdict.includes(upsellId))) return "Processing";
                    return "Fail";
                };
                return { skill: statusOf("skill"), incomeKit: statusOf("incomeKit") };
            },
            target: $statuses
        });

        // Owned already: leave the offer (outside the first-login funnel, whose
        // navigation moves on by itself).
        sample({
            source: {
                statuses: $statuses,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) => {
                const product = getScreenProduct(s.currentUpsellId);
                const status = product ? s.statuses[product] : null;
                return (status === "Success" || status === "Processing") && s.isUpsellRoute && !s.firstLoginFlag;
            },
            target: [this.upsellUiModule.events.packAccessRedirectTriggered, buyButtonDisabled]
        });

        sample({
            source: {
                statuses: $statuses,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive
            },
            filter: (s) => {
                const product = getScreenProduct(s.currentUpsellId);
                return product !== null && s.statuses[product] === "Fail" && s.isUpsellRoute;
            },
            target: buyButtonReset
        });

        sample({
            clock: $statuses,
            source: { isUpsellRoute: this.routesModule.stores.upsell.$isActive },
            filter: (s, statuses) =>
                !s.isUpsellRoute && (statuses.skill === "Processing" || statuses.incomeKit === "Processing"),
            target: pollingStarted
        });

        sample({
            clock: $statuses,
            filter: (statuses) => statuses.skill !== "Processing" && statuses.incomeKit !== "Processing",
            target: pollingStopped
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.finally,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: checkFinished
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.fail,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: [pollingStarted, buyButtonReset]
        });

        sample({
            clock: tick,
            source: { upsellIds: $upsellIds, isCheckPending: $isCheckPending },
            filter: (s) => s.upsellIds.length > 0 && !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        reset({
            clock: routePathNameChanged,
            target: [$isDiscountModalOpen, $statuses, $isCheckRequested]
        });
    }
}
